import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type {
  FleetInstallation,
  InstallationSigningKey,
  BackupPosture,
  RotateKeyRequest,
  RevokeCompromisedKeyRequest,
} from '@hospasense/contracts';
import {
  fetchInstallation,
  updateInstallation,
  fetchSigningKeys,
  rotateSigningKey,
  revokeSigningKey,
  fetchBackupPosture,
  fetchRecentHeartbeats,
} from '../lib/queries-fleet.js';
import { RotateKeyModal, RevokeKeyModal } from './fleet-modals.js';
import { useAuth } from '../lib/auth.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Button,
  Card,
  CardBody,
  PageHeader,
  Badge,
  StatusPill,
  Table,
  TableContainer,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingState,
  ErrorState,
} from '../components/ui/index.js';

export function InstallationDetailPage() {
  const { identifier } = useParams<{ identifier: string }>();
  const { hasPermission } = useAuth();
  const canRotate = hasPermission('fleet.rotate_keys');

  const [installation, setInstallation] = useState<FleetInstallation | null>(null);
  const [keys, setKeys] = useState<InstallationSigningKey[]>([]);
  const [backup, setBackup] = useState<BackupPosture | null>(null);
  const [heartbeats, setHeartbeats] = useState<
    Array<{ id: string; timestamp: string; schemaVersion: string; payload: unknown }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isRotateOpen, setIsRotateOpen] = useState(false);
  const [revokeKeyId, setRevokeKeyId] = useState<string | null>(null);

  const loadAll = async () => {
    if (!identifier) return;
    setLoading(true);
    try {
      const [instData, keysData, backupData, heartbeatsData] = await Promise.all([
        fetchInstallation(identifier),
        fetchSigningKeys(identifier),
        fetchBackupPosture(identifier).catch(() => null),
        fetchRecentHeartbeats(identifier).catch(() => []),
      ]);
      setInstallation(instData);
      setKeys(keysData);
      setBackup(backupData);
      setHeartbeats(heartbeatsData);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
  }, [identifier]);

  const handleRingChange = async (newRing: 'internal' | 'pilot' | 'general') => {
    if (!installation || !identifier) return;
    try {
      const updated = await updateInstallation(identifier, {
        rolloutRing: newRing,
        expectedVersion: installation.version,
      });
      setInstallation(updated);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleRotateKey = async (data: RotateKeyRequest) => {
    if (!identifier) return;
    await rotateSigningKey(identifier, data);
    await loadAll();
  };

  const handleRevokeKey = async (data: RevokeCompromisedKeyRequest) => {
    if (!identifier) return;
    await revokeSigningKey(identifier, data);
    await loadAll();
  };

  if (loading) {
    return <LoadingState label="Loading installation details..." />;
  }

  if (error || !installation) {
    return (
      <ErrorState
        description={error ?? 'Installation not found'}
        onRetry={() => {
          setError(null);
          void loadAll();
        }}
      />
    );
  }

  const statusTone =
    installation.connectivityState === 'connected'
      ? 'success'
      : installation.connectivityState === 'degraded'
        ? 'warning'
        : 'error';

  return (
    <div className="space-y-6">
      <PageHeader
        title={installation.displayName}
        subtitle={`Installation ID: ${installation.installationIdentifier}`}
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: 'Fleet', to: '/fleet' },
              { label: installation.installationIdentifier },
            ]}
          />
        }
        actions={
          canRotate && (
            <Button variant="primary" onClick={() => setIsRotateOpen(true)}>
              Rotate Signing Key
            </Button>
          )
        }
      />

      {/* Overview Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <CardBody>
            <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
              Status & Ring
            </span>
            <div className="mt-2 flex items-center justify-between">
              <StatusPill tone={statusTone} label={installation.connectivityState} />
              <select
                value={installation.rolloutRing}
                onChange={(e) =>
                  handleRingChange(e.target.value as 'internal' | 'pilot' | 'general')
                }
                className="rounded-lg border border-border bg-bg-s1 px-2.5 py-1 text-xs font-semibold text-text-primary focus:border-border focus:ring-1 focus:ring-accent focus:outline-none"
              >
                <option value="internal">Ring: Internal</option>
                <option value="pilot">Ring: Pilot</option>
                <option value="general">Ring: General</option>
              </select>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
              Version & Topology
            </span>
            <div className="mt-2 flex items-center justify-between">
              <div>
                <span className="font-mono text-sm font-bold text-text-primary">
                  {installation.installedVersion}
                </span>
                <span className="ml-2 text-xs text-text-muted">
                  ({installation.deploymentProfile.replace(/_/g, ' ')})
                </span>
              </div>
              <span className="text-xs text-text-muted">{installation.region}</span>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
              Backup SLA Posture
            </span>
            <div className="mt-2 flex items-center justify-between">
              {backup ? (
                <>
                  <div>
                    <Badge tone={backup.verificationStatus === 'verified' ? 'success' : 'warning'}>
                      {backup.verificationStatus}
                    </Badge>
                    <div className="text-xs text-text-muted mt-1">
                      Target: {backup.backupTarget}
                    </div>
                  </div>
                  <div className="text-right text-xs text-text-muted">
                    <div>SLA: {backup.freshnessSlaHours}h</div>
                    <div className="tabular-nums">
                      {(backup.backupSizeBytes / (1024 * 1024 * 1024)).toFixed(1)} GB
                    </div>
                  </div>
                </>
              ) : (
                <span className="text-xs text-text-muted">No backup profile configured</span>
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Component Health & Integration Readiness */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardBody>
            <h2 className="text-sm font-bold uppercase tracking-wider text-text-secondary">
              Component Health Status
            </h2>
            <div className="mt-4 space-y-2">
              {installation.componentHealth.length === 0 ? (
                <p className="text-xs text-text-muted">
                  No component health telemetry received yet.
                </p>
              ) : (
                installation.componentHealth.map((comp) => (
                  <div
                    key={comp.component}
                    className="flex items-center justify-between rounded-lg border border-border bg-bg-s1 p-2.5"
                  >
                    <span className="font-mono text-xs text-text-primary">{comp.component}</span>
                    <Badge
                      tone={
                        comp.status === 'healthy'
                          ? 'success'
                          : comp.status === 'degraded'
                            ? 'warning'
                            : 'error'
                      }
                    >
                      {comp.status}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h2 className="text-sm font-bold uppercase tracking-wider text-text-secondary">
              Integration Readiness Flags
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {Object.entries(installation.integrationReadiness).map(([k, ready]) => (
                <div
                  key={k}
                  className="flex items-center justify-between rounded-lg border border-border bg-bg-s1 p-2.5"
                >
                  <span className="font-mono text-xs text-text-secondary">{k}</span>
                  <span
                    className={`text-xs font-semibold ${
                      ready ? 'text-success' : 'text-text-muted'
                    }`}
                  >
                    {ready ? 'Ready ✓' : 'Not Ready'}
                  </span>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Signing Keys Section */}
      <Card>
        <CardBody>
          <h2 className="text-sm font-bold uppercase tracking-wider text-text-secondary">
            Cryptographic Signing Keys
          </h2>
          <div className="mt-4">
            <TableContainer>
              <Table density="compact">
                <THead>
                  <TR>
                    <TH>Key ID</TH>
                    <TH>Algorithm</TH>
                    <TH>Status</TH>
                    <TH>Issued / Expires</TH>
                    <TH align="right">Actions</TH>
                  </TR>
                </THead>
                <TBody>
                  {keys.map((k) => (
                    <TR key={k.id}>
                      <TD className="font-mono">{k.keyId}</TD>
                      <TD className="font-mono">{k.algorithm}</TD>
                      <TD>
                        <Badge
                          tone={
                            k.status === 'active'
                              ? 'success'
                              : k.status === 'retired'
                                ? 'neutral'
                                : 'error'
                          }
                        >
                          {k.status}
                        </Badge>
                      </TD>
                      <TD>
                        <div className="text-xs text-text-muted">
                          Issued: {new Date(k.issuedAt).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-text-muted">
                          Expires: {new Date(k.expiresAt).toLocaleDateString()}
                        </div>
                      </TD>
                      <TD align="right">
                        {k.status === 'active' && canRotate && (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => setRevokeKeyId(k.keyId)}
                          >
                            Revoke
                          </Button>
                        )}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableContainer>
          </div>
        </CardBody>
      </Card>

      {/* Live Telemetry Stream */}
      <Card>
        <CardBody>
          <h2 className="text-sm font-bold uppercase tracking-wider text-text-secondary">
            Recent Signed Telemetry Stream (Non-PHI)
          </h2>
          <div className="mt-4">
            {heartbeats.length === 0 ? (
              <p className="text-xs text-text-muted">No telemetry recorded yet.</p>
            ) : (
              <TableContainer>
                <Table density="compact">
                  <THead>
                    <TR>
                      <TH>Timestamp</TH>
                      <TH>Schema</TH>
                      <TH>Summary Metrics</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {heartbeats.map((hb) => (
                      <TR key={hb.id}>
                        <TD className="font-mono">{new Date(hb.timestamp).toLocaleString()}</TD>
                        <TD className="font-mono">{hb.schemaVersion}</TD>
                        <TD className="font-mono">{JSON.stringify(hb.payload).slice(0, 100)}...</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableContainer>
            )}
          </div>
        </CardBody>
      </Card>

      <RotateKeyModal
        isOpen={isRotateOpen}
        onClose={() => setIsRotateOpen(false)}
        onSubmit={handleRotateKey}
      />

      {revokeKeyId && (
        <RevokeKeyModal
          isOpen={Boolean(revokeKeyId)}
          keyId={revokeKeyId}
          onClose={() => setRevokeKeyId(null)}
          onSubmit={handleRevokeKey}
        />
      )}
    </div>
  );
}
